import {
  STUDENT_WORKBOOK_LIST_TYPE,
  type StudentWorkbookDetailPathParamT,
  studentWorkbookListPathParams,
} from '~/@entities/StudentWorkbook/StudentWorkbook.schema'

export const studentWorkbookDetailPathParams: Record<
  keyof StudentWorkbookDetailPathParamT,
  keyof StudentWorkbookDetailPathParamT
> = {
  studentWorkbookId: 'studentWorkbookId',
  revisionId: 'revisionId',
  pageNumber: 'pageNumber',
} as const

// 학생 id로 사용할 수 있는 route는 /student로, 학부모 id로 사용할 수 있는 route는 /parent로 시작
export const routeName = {
  login: '/login', // 로그인

  // b2g 외부 SSO/로그인 진입점
  // ⚠️ 경로값은 외부 OAuth redirect_uri로 파트너사 시스템에 등록되어 있음 — 절대 변경 금지
  auth: {
    whalespace: '/whalespace', // 네이버 웨일스페이스 진입
    aiOnOAuthLogin: '/whale/connect', // 웨일스페이스 SSO 로그인 요청 경로
    aiOnOAuthCallback: '/whale/callback', // 웨일스페이스 SSO 로그인 콜백 (redirect_uri)
    aiepBridge: '/aiep/bridge', // 광주 AIEP 통합 로그인 브릿지
    guestLogin: '/83f7e1b6-5c2d-4f59-9342-9e5c83a6d0de', // 외부 시연용 magic 로그인 경로
  },

  profile: '/profile',

  // QR 학습지 (선생님이 만든 학습지를 학생이 QR 로 진입해 채점)
  qrWorksheet: {
    defaultPath: `/worksheet`,
    // 미출제된 학습지를 출제하기 위한 페이지
    get scoring() {
      return `${this.defaultPath}/:worksheetId/:dateTime/scoring` as const
    },
  },

  // 학생
  student: {
    defaultPath: `/student`,

    get challenge() {
      return `${this.defaultPath}/challenge`
    },
    get studentWorkbook() {
      return `${this.defaultPath}/student-workbook`
    },
    get studentWorkbookList() {
      return `${this.defaultPath}/student-workbook/list/:${studentWorkbookListPathParams.studentWorkbookListType}`
    },
    get generalStudentWorkbookList() {
      return `${this.defaultPath}/student-workbook/list/${STUDENT_WORKBOOK_LIST_TYPE.일반교재}`
    },
    get customSignatureStudentWorkbookList() {
      return `${this.defaultPath}/student-workbook/list/${STUDENT_WORKBOOK_LIST_TYPE.시그니처교재}`
    },
    get academyGroupStudentWorkbookList() {
      return `${this.defaultPath}/student-workbook/list/${STUDENT_WORKBOOK_LIST_TYPE.학원그룹교재}`
    },
    get studentWorkbookDetail() {
      return `${this.defaultPath}/student-workbook/detail/:${studentWorkbookDetailPathParams.studentWorkbookId}/:${studentWorkbookDetailPathParams.revisionId}/:${studentWorkbookDetailPathParams.pageNumber}`
    },
    get studentWorkbookViewer() {
      return `${this.defaultPath}/student-workbook/viewer/:${studentWorkbookDetailPathParams.studentWorkbookId}/:${studentWorkbookDetailPathParams.revisionId}/:${studentWorkbookDetailPathParams.pageNumber}`
    },
    get studentWorksheet() {
      return `${this.defaultPath}/student-worksheet` as const
    },
    get studentWorksheetById() {
      return `${this.defaultPath}/student-worksheet/:studentWorksheetId`
    },
    get studentWorksheetScoringById() {
      return `${this.defaultPath}/student-worksheet/:studentWorksheetId/scoring`
    },
    get studentExamScoringById() {
      return `${this.defaultPath}/student-exam/:studentExamId/scoring`
    },
    get cas() {
      return `${this.defaultPath}/cas`
    },
    get casByAssignedId() {
      return `${this.defaultPath}/cas/:assignedId`
    },
    get lecture() {
      return `${this.defaultPath}/lecture`
    },
    get lectureList() {
      return `${this.defaultPath}/lecture/list`
    },
    get lectureDetail() {
      return `${this.defaultPath}/lecture/:lectureId`
    },
    get assignedLectureDetail() {
      return `${this.defaultPath}/lecture/:lectureId/:assignmentId`
    },
    get signatureWorkbookLectureList() {
      return `${this.defaultPath}/lecture/signature-workbook/list` // 시그니처 교재 전용 강의 리스트
    },
    get signatureWorkbookLectureDetail() {
      return `${this.defaultPath}/lecture/signature-workbook/:lectureId` // 시그니처 교재 전용 강의 리스트
    },
    get conceptualLearning() {
      return `${this.defaultPath}/conceptual-learning/:studentWorksheetId`
    },
    get conceptLearning() {
      // conceptualLearning으로 redirect, 삭제 예정
      return `${this.defaultPath}/concept-learning/:studentWorksheetId`
    },
    // 학생-자기주도학습
    get studentLearningProcess() {
      return `${this.defaultPath}/learning-process/:studentWorksheetId`
    },
  },

  // 학생-학습지
  // studentWorksheetList: '/student/student-worksheet', // 학습지 리스트
  // studentWorksheet: '/student/worksheet/:studentWorksheetId', // 학습지
  // studentWorksheetScoring: '/student/worksheet/:studentWorksheetId/scoring', // 학습지 채점 결과&학습지 빠른 채점
  // worksheetScoringByQR: '/worksheet/:worksheetId/:dateTime/scoring', // qr을 통한 학습지 빠른 채점
  // studentExam: '/student-exam/:studentExamId', // 시험지
  // studentExamScoring: '/student-exam/:studentExamId/scoring', // 시험지 채점 결과&시험지 빠른 채점

  // 학부모
  parent: {
    defaultPath: '/parent',
    get attendance() {
      return `${this.defaultPath}/attendance`
    },
    get report() {
      return `${this.defaultPath}/report`
    },
    get notice() {
      return `${this.defaultPath}/notice`
    },
    get noticeDetail() {
      return `${this.defaultPath}/notice/:noticeId`
    },
    // 학습 카드 진입 — 학부모는 학습지/시험은 항상 scoring view
    get studentWorksheetScoringById() {
      return `${this.defaultPath}/student-worksheet/:studentWorksheetId/scoring`
    },
    get studentExamScoringById() {
      return `${this.defaultPath}/student-exam/:studentExamId/scoring`
    },
    get studentWorkbookViewer() {
      return `${this.defaultPath}/student-workbook/viewer/:${studentWorkbookDetailPathParams.studentWorkbookId}/:${studentWorkbookDetailPathParams.revisionId}/:${studentWorkbookDetailPathParams.pageNumber}`
    },
    get studentWorkbookDetail() {
      return `${this.defaultPath}/student-workbook/detail/:${studentWorkbookDetailPathParams.studentWorkbookId}/:${studentWorkbookDetailPathParams.revisionId}/:${studentWorkbookDetailPathParams.pageNumber}`
    },
  },

  // passwordChange: '/profile/password-change', // 내 정보 - 비밀번호 변경
  // parentNotice: '/profile/parent-notice', // (학부모) 내 정보 - 학원 공지

  guideVideo: '/guide-video', // 가이드 동영상 보기 페이지
  notification: '/notification', // 푸시 알림 페이지

  // 통합 마이그레이션 placeholder 라우트 — 실제 로직은 index.html 인라인.
  //   sync        : (sender→receiver 단방향) receiver 가 띄운 iframe 안에서 storage 통째 송신 (REQUEST_SYNC 응답)
  //   logoutClear : (양방향) 명시 로그아웃 시 *상대* origin 의 storage clear (LOGOUT_CLEAR 응답)
  //   probe       : sender 가 띄운 invisible iframe 이 자발 PROBE_RESPONSE 송신 (receiver 배포 확인용)
  //   devReset    : receiver dev panel 이 띄운 iframe 안에서 sender 의 TRANSFERRED marker 제거 (라이브 외 한정)
  integration: {
    sync: '/integration/sync',
    logoutClear: '/integration/logout-clear',
    probe: '/integration/probe',
    devReset: '/integration/dev-reset',
  },

  // 브릿지 페이지 (ProtectedRoute 밖 — 네이티브 앱 실행 또는 외부 진입이 목적)
  bridge: {
    teacherIframe: '/bridge/teacher-iframe',
    qr: '/qr',
    lecture: '/lectures',
    signatureWorkbookLecture: '/signature-workbook-lectures/:signatureType',
  },

  serverMaintenance: '/server-maintenance', // 서버 점검 페이지
} as const

export const fullPageRoutes = {
  has(pathname: string) {
    return new Set<string>([routeName.login, routeName.bridge.teacherIframe]).has(pathname)
  },
}

// ProtectedRoute 밖 경로 — 인증 없이 진입 가능한 페이지들
// 세션 만료/로그아웃 broadcast 수신 시 /login으로 튕기면 안 되는 경로 목록
// ⚠️ appRouter.tsx의 ProtectedRoute 밖 경로와 동기화 유지 필요
export const publicRoutes = {
  has(pathname: string) {
    return (
      pathname === routeName.login ||
      pathname === routeName.bridge.teacherIframe ||
      pathname === routeName.auth.whalespace ||
      pathname === routeName.auth.aiOnOAuthLogin ||
      pathname === routeName.auth.aiOnOAuthCallback ||
      pathname === routeName.auth.aiepBridge ||
      pathname === routeName.auth.guestLogin ||
      pathname === routeName.bridge.lecture ||
      pathname.startsWith('/signature-workbook-lectures') ||
      pathname === routeName.bridge.qr ||
      pathname === routeName.integration.sync ||
      pathname === routeName.integration.logoutClear ||
      pathname === routeName.integration.probe ||
      pathname === routeName.integration.devReset ||
      pathname === routeName.serverMaintenance
    )
  },
}
